import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation } from "react-router-dom";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Typography from "@mui/material/Typography";

const LANGUAGES = [
  { code: "en", flag: "🇨🇦", label: "English" },
  { code: "fr", flag: "🇨🇦", label: "Français" },
  { code: "zh", flag: "🇨🇳", label: "简体中文" },
  { code: "zh-TW", flag: "🇹🇼", label: "繁體中文" },
  { code: "pa", flag: "🇮🇳", label: "ਪੰਜਾਬੀ" },
  { code: "es", flag: "🇪🇸", label: "Español" },
  { code: "tl", flag: "🇵🇭", label: "Filipino" },
  { code: "ar", flag: "🇸🇦", label: "العربية" },
  { code: "ko", flag: "🇰🇷", label: "한국어" },
  { code: "ja", flag: "🇯🇵", label: "日本語" },
];

const NON_EN_LANGS = ["fr", "zh", "zh-TW", "pa", "es", "tl", "ar", "ko", "ja"];

function stripLangPrefix(pathname) {
  const segments = pathname.split("/").filter(Boolean);
  if (segments.length > 0 && NON_EN_LANGS.includes(segments[0])) {
    return "/" + segments.slice(1).join("/");
  }
  return pathname;
}

function LanguageSwitcher() {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const currentLang =
    LANGUAGES.find((l) => l.code === i18n.language) ||
    LANGUAGES.find((l) => l.code === i18n.language?.split("-")[0]) ||
    LANGUAGES[0];

  const handleOpen = (event) => setAnchorEl(event.currentTarget);
  const handleClose = () => setAnchorEl(null);
  const handleSelect = (code) => {
    const basePath = stripLangPrefix(location.pathname);
    if (code === "en") {
      navigate(basePath || "/");
    } else {
      navigate(`/${code}${basePath}`);
    }
    i18n.changeLanguage(code);
    handleClose();
  };

  return (
    <>
      <IconButton
        onClick={handleOpen}
        size="small"
        dir="ltr"
        sx={{
          borderRadius: "8px",
          border: "1px solid rgba(0,0,0,0.12)",
          px: 1.5,
          py: 0.5,
          color: "#344767",
          backgroundColor: "rgba(255, 255, 255, 0.9)",
          "&:hover": { backgroundColor: "rgba(255, 255, 255, 1)" },
        }}
      >
        <Typography
          variant="body2"
          component="span"
          sx={{
            fontSize: "0.85rem",
            lineHeight: 1,
            fontWeight: 500,
            display: "flex",
            alignItems: "center",
            gap: 0.5,
          }}
        >
          <span>{currentLang.flag}</span>
          <span>{currentLang.label}</span>
        </Typography>
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        PaperProps={{
          sx: {
            borderRadius: "12px",
            mt: 1,
            minWidth: 160,
            boxShadow: "0 4px 20px rgba(0,0,0,0.12)",
            direction: "ltr",
          },
        }}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
      >
        {LANGUAGES.map((lang) => (
          <MenuItem
            key={lang.code}
            selected={lang.code === currentLang.code}
            onClick={() => handleSelect(lang.code)}
            dir="ltr"
            sx={{
              py: 0.75,
              px: 2,
              borderRadius: "8px",
              mx: 0.5,
              "&.Mui-selected": {
                backgroundColor: "rgba(0,0,0,0.06)",
                "&:hover": { backgroundColor: "rgba(0,0,0,0.09)" },
              },
            }}
          >
            <Typography
              variant="body2"
              component="span"
              sx={{ fontSize: "0.875rem", display: "flex", alignItems: "center", gap: 1 }}
            >
              <span style={{ direction: "ltr" }}>{lang.flag}</span>
              <span>{lang.label}</span>
            </Typography>
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}

export default LanguageSwitcher;
